import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import logo from '../images/logo.png';

// Mirrors the surface/brand tokens in tailwind.config.js
const colors = {
  base: '#0B1220',
  raised: '#111A2E',
  line: '#1E2A44',
  brand: '#3B82F6',
  brandLight: '#60A5FA',
  brandDark: '#1D4ED8',
  ink: '#F8FAFC',
  inkFaint: '#64748B',
};

const BRAND = 'AC TECH INSTITUTE';

// Progress ring geometry
const RING_SIZE = 180;
const RING_RADIUS = 82;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

// How long the intro runs, start to finish
const DURATION_MS = 1800;

const Preloader = ({ onLoadingComplete }) => {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Progress is derived from elapsed time rather than accumulated per tick,
    // so a throttled timer (background tab) still finishes on schedule.
    const startedAt = Date.now();
    let tick;
    let exit;

    const step = () => {
      const elapsed = Date.now() - startedAt;
      const linear = Math.min(elapsed / DURATION_MS, 1);
      const eased = 1 - Math.pow(1 - linear, 3); // ease-out cubic
      setProgress(eased * 100);

      if (linear < 1) {
        tick = setTimeout(step, 28);
        return;
      }

      setLoading(false);
      exit = setTimeout(() => {
        if (onLoadingComplete) onLoadingComplete();
      }, 700);
    };

    step();

    return () => {
      clearTimeout(tick);
      clearTimeout(exit);
    };
  }, [onLoadingComplete]);

  const shown = Math.round(progress);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden"
      style={{ backgroundColor: colors.base }}
      animate={{ opacity: loading ? 1 : 0, scale: loading ? 1 : 1.03 }}
      transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Aurora glows */}
      <motion.div
        className="absolute -top-40 -left-32 w-[36rem] h-[36rem] rounded-full blur-3xl"
        style={{ background: colors.brand, opacity: 0.18 }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.14, 0.22, 0.14] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute -bottom-40 -right-24 w-[32rem] h-[32rem] rounded-full blur-3xl"
        style={{ background: colors.brandDark, opacity: 0.16 }}
        animate={{ scale: [1.1, 1, 1.1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Grid texture + vignette */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNMzAgMzBoMzB2MzBIMzB6IiBmaWxsPSIjM0I4MkY2IiBmaWxsLW9wYWNpdHk9Ii4wOCIvPjxwYXRoIGQ9Ik0wIDMwaDMwdjMwSDB6IiBmaWxsPSIjM0I4MkY2IiBmaWxsLW9wYWNpdHk9Ii4wOCIvPjwvZz48L3N2Zz4=')] opacity-40 pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 45%, transparent 30%, ${colors.base} 85%)` }}
      />

      <div className="relative flex flex-col items-center px-8">
        {/* Ring + logo */}
        <div className="relative flex items-center justify-center" style={{ width: RING_SIZE, height: RING_SIZE }}>
          {/* Slow orbiting dashed ring */}
          <motion.div
            className="absolute inset-0 rounded-full border border-dashed"
            style={{ borderColor: `${colors.brandLight}33` }}
            animate={{ rotate: 360 }}
            transition={{ duration: 24, repeat: Infinity, ease: 'linear' }}
          />

          {/* Progress ring */}
          <svg
            className="absolute inset-0 -rotate-90"
            width={RING_SIZE}
            height={RING_SIZE}
            viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          >
            <defs>
              <linearGradient id="preloader-ring" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={colors.brandLight} />
                <stop offset="55%" stopColor={colors.brand} />
                <stop offset="100%" stopColor={colors.brandDark} />
              </linearGradient>
            </defs>
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              stroke={'rgba(255,255,255,0.10)'}
              strokeWidth="3"
            />
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              stroke="url(#preloader-ring)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress / 100)}
              style={{ transition: 'stroke-dashoffset 0.25s linear' }}
            />
          </svg>

          {/* Glass disc + logo */}
          <motion.div
            className="relative flex items-center justify-center w-28 h-28 rounded-full backdrop-blur-sm"
            style={{
              background: `linear-gradient(145deg, ${colors.line}, ${colors.raised})`,
              boxShadow: `0 0 40px ${colors.brand}40, inset 0 1px 0 ${'rgba(255,255,255,0.20)'}`,
              border: `1px solid ${'rgba(255,255,255,0.10)'}`
            }}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
            transition={{
              opacity: { duration: 0.6 },
              scale: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
              y: { duration: 4, repeat: Infinity, ease: 'easeInOut' }
            }}
          >
            <img src={logo} alt="AC Tech Institute" className="w-16 h-16 object-contain" />
          </motion.div>
        </div>

        {/* Wordmark */}
        <div className="mt-10 flex" role="img" aria-label={BRAND}>
          {BRAND.split('').map((char, i) => (
            <motion.span
              key={`${char}-${i}`}
              aria-hidden="true"
              className="text-xl md:text-2xl font-semibold text-white"
              style={{ letterSpacing: '0.22em', whiteSpace: 'pre' }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 + i * 0.035, duration: 0.5, ease: 'easeOut' }}
            >
              {char}
            </motion.span>
          ))}
        </div>

        <motion.p
          className="mt-3 text-sm tracking-[0.35em] uppercase"
          style={{ color: colors.inkFaint }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
        >
          Transforming Education
        </motion.p>

        {/* Hairline shimmer */}
        <motion.div
          className="relative mt-8 h-px w-56 overflow-hidden"
          style={{ backgroundColor: 'rgba(255,255,255,0.10)' }}
          initial={{ opacity: 0, scaleX: 0.4 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ delay: 0.8, duration: 0.7 }}
        >
          <motion.div
            className="absolute top-0 h-px w-1/3"
            style={{ background: `linear-gradient(90deg, transparent, ${colors.brandLight}, transparent)` }}
            animate={{ x: ['-100%', '300%'] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>

        {/* Percentage */}
        <motion.div
          className="mt-5 flex items-baseline gap-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.6 }}
        >
          <span
            className="text-3xl font-light tabular-nums"
            style={{ color: colors.ink }}
          >
            {shown}
          </span>
          <span className="text-sm" style={{ color: colors.inkFaint }}>%</span>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Preloader;
